import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { DomainApiError } from '../lib/api-error.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { listOffices, type OfficeDto } from '../lib/offices-api.js';
import {
  listCaseCommissionShares,
  setCaseCommissionShares,
  type CommissionShareInput,
  type PayoutRecipientType,
} from '../lib/commissions-api.js';
import { CzechNumberInput } from './czech-number-input.js';

/**
 * Krok PROV1 bod 1 — rozpad provize u zakázky (makléř / pobočka / kancelář),
 * procentem nebo pevnou částkou, víc makléřů na jedné zakázce.
 *
 * Stojí na záložce **Obchod → Provize** u dokladů zakázky (`CaseInvoicesPanel`,
 * FAK2 bod 4): rozpad se zadává tam, kde se provize fakturuje.
 *
 * Rozpad se posílá jako CELÁ SADA (`PUT`) — je to jedna věta („40 % makléř,
 * 10 % pobočka, zbytek kancelář"), ne nezávislé řádky; po řádcích by mezi
 * dvěma voláními existoval stav, který dává dohromady víc než 100 %.
 */

const LOCALE = 'cs' as const;

interface Row extends CommissionShareInput {
  /** Klíč pro React — řádek ještě nemusí mít id ze serveru. */
  readonly key: string;
}

function emptyRow(index: number): Row {
  return { key: `new-${String(index)}`, recipientType: 'broker', brokerUserId: null, officeId: null, label: null, basisType: 'percent', basisValue: '' };
}

export function CommissionSharesCard({ caseId }: { readonly caseId: string }): React.JSX.Element {
  const [rows, setRows] = React.useState<readonly Row[] | null>(null);
  const [members, setMembers] = React.useState<readonly TenantMember[]>([]);
  const [offices, setOffices] = React.useState<readonly OfficeDto[]>([]);
  const [note, setNote] = React.useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    void listCaseCommissionShares(caseId)
      .then((items) =>
        setRows(
          items.map((item) => ({
            key: item.id,
            recipientType: item.recipientType,
            brokerUserId: item.brokerUserId,
            officeId: item.officeId,
            label: item.label,
            basisType: item.basisType,
            basisValue: item.basisValue,
          })),
        ),
      )
      .catch(() => setRows([]));
    void listMembers().then(setMembers).catch(() => setMembers([]));
    void listOffices().then(setOffices).catch(() => setOffices([]));
  }, [caseId]);

  function patch(key: string, change: Partial<CommissionShareInput>): void {
    setRows((current) => (current ?? []).map((row) => (row.key === key ? { ...row, ...change } : row)));
  }

  async function save(): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      const payload: CommissionShareInput[] = (rows ?? [])
        .filter((row) => row.basisValue.trim() !== '')
        .map((row) => ({
          recipientType: row.recipientType,
          brokerUserId: row.recipientType === 'broker' ? row.brokerUserId : null,
          officeId: row.officeId,
          label: row.label === null || row.label.trim() === '' ? null : row.label.trim(),
          basisType: row.basisType,
          basisValue: row.basisValue.trim(),
        }));
      await setCaseCommissionShares(caseId, payload);
      setNote({ ok: true, text: t('webAdmin.provize.sharesSaved', { locale: LOCALE }) });
    } catch (error) {
      setNote({ ok: false, text: error instanceof DomainApiError ? error.message : t('error.internal', { locale: LOCALE }) });
    } finally {
      setBusy(false);
    }
  }

  const recipientLabel = (type: PayoutRecipientType): string => {
    const key = type === 'broker' ? 'recipientBroker' : type === 'office' ? 'recipientOffice' : 'recipientAgency';
    return t(`webAdmin.provize.${key}` as MessageKey, { locale: LOCALE });
  };

  return (
    <section className="rh-a2-card" data-testid="commission-shares-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.provize.sharesTitle', { locale: LOCALE })}</h2>
      </div>
      <p className="rh-a2-sub">{t('webAdmin.provize.sharesHelp', { locale: LOCALE })}</p>

      {rows !== null && rows.length === 0 && (
        <p className="rh-a2-muted" data-testid="commission-shares-empty">{t('webAdmin.provize.sharesEmpty', { locale: LOCALE })}</p>
      )}

      {/* Na 390 px se řádek láme pod sebe (`flex-wrap`), na 1440 px stojí v jedné lince. */}
      {(rows ?? []).map((row) => (
        <div
          key={row.key}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginTop: 8 }}
          data-testid={`commission-share-row-${row.key}`}
        >
          <label className="rh-a2-field">
            {t('webAdmin.provize.sharesRecipient', { locale: LOCALE })}
            <select
              value={row.recipientType}
              onChange={(e) => patch(row.key, { recipientType: e.target.value as PayoutRecipientType })}
              data-testid={`commission-share-type-${row.key}`}
            >
              {(['broker', 'office', 'agency'] as const).map((type) => (
                <option key={type} value={type}>{recipientLabel(type)}</option>
              ))}
            </select>
          </label>

          {row.recipientType === 'broker' && (
            <label className="rh-a2-field">
              {t('webAdmin.provize.sharesBroker', { locale: LOCALE })}
              <select
                value={row.brokerUserId ?? ''}
                onChange={(e) => {
                  const userId = e.target.value === '' ? null : e.target.value;
                  const member = members.find((m) => m.userId === userId);
                  patch(row.key, { brokerUserId: userId, label: member?.name ?? member?.email ?? row.label });
                }}
                data-testid={`commission-share-broker-${row.key}`}
              >
                <option value="">{t('webAdmin.provize.sharesNoBroker', { locale: LOCALE })}</option>
                {members.map((member) => (
                  <option key={member.userId} value={member.userId}>{member.name ?? member.email}</option>
                ))}
              </select>
            </label>
          )}

          {offices.length > 0 && (
            <label className="rh-a2-field">
              {t('webAdmin.provize.sharesOffice', { locale: LOCALE })}
              <select
                value={row.officeId ?? ''}
                onChange={(e) => patch(row.key, { officeId: e.target.value === '' ? null : e.target.value })}
                data-testid={`commission-share-office-${row.key}`}
              >
                <option value="">{t('webAdmin.provize.sharesNoOffice', { locale: LOCALE })}</option>
                {offices.map((office) => (
                  <option key={office.id} value={office.id}>{office.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="rh-a2-field">
            {t('webAdmin.provize.sharesBasis', { locale: LOCALE })}
            <select
              value={row.basisType}
              onChange={(e) => patch(row.key, { basisType: e.target.value === 'fixed' ? 'fixed' : 'percent' })}
              data-testid={`commission-share-basis-${row.key}`}
            >
              <option value="percent">{t('webAdmin.provize.sharesBasisPercent', { locale: LOCALE })}</option>
              <option value="fixed">{t('webAdmin.provize.sharesBasisFixed', { locale: LOCALE })}</option>
            </select>
          </label>

          <label className="rh-a2-field">
            {t('webAdmin.provize.sharesValue', { locale: LOCALE })}
            {/*
              Ne `type="number"`: částka i procento jde na server jako numeric
              string (Money, CLAUDE.md §3), `number` v prohlížeči zaokrouhluje
              a v některých lokalizacích mění desetinný oddělovač.
              Krok UXD2: a ne surový text — „15 000,50" šlo doteď na server
              tak, jak ho člověk napsal, a skončilo 422. `CzechNumberInput`
              pole přečte a navenek dá kanonické `15000.5`.
            */}
            <CzechNumberInput
              value={row.basisValue}
              onValueChange={(next) => patch(row.key, { basisValue: next })}
              testId={`commission-share-value-${row.key}`}
            />
          </label>

          <button
            type="button"
            className="rh-a2-btn"
            onClick={() => setRows((current) => (current ?? []).filter((r) => r.key !== row.key))}
            data-testid={`commission-share-remove-${row.key}`}
          >
            {t('webAdmin.provize.sharesRemove', { locale: LOCALE })}
          </button>
        </div>
      ))}

      <p className="rh-a2-card__foot" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          className="rh-a2-btn"
          onClick={() => setRows((current) => [...(current ?? []), emptyRow((current ?? []).length)])}
          data-testid="commission-share-add"
        >
          {t('webAdmin.provize.sharesAdd', { locale: LOCALE })}
        </button>
        <button type="button" className="rh-a2-btn rh-a2-btn--primary" disabled={busy || rows === null} onClick={() => void save()} data-testid="commission-shares-save">
          {t('webAdmin.provize.sharesSave', { locale: LOCALE })}
        </button>
      </p>

      {note !== null && (
        <p role={note.ok ? 'status' : 'alert'} className={note.ok ? 'rh-a2-status' : 'rh-a2-error'} data-testid="commission-shares-note">
          {note.text}
        </p>
      )}
    </section>
  );
}
